import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';
import { useSEO } from '../../utils/seo';

const PricingPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-pet-funeral.example.com';

  useSEO({
    title: '料金表｜岡山市のペット火葬・納骨料金｜おかやまペット葬儀株式会社',
    description: '岡山市のペット葬儀料金表。火葬料金15,000円から、納骨料金10,000円から。小鳥、ハムスター、猫、小型犬、大型犬対応。個別火葬、合同火葬、納骨、仏具販売の明瞭な料金体系。',
    keywords: '岡山市,ペット葬儀,料金表,火葬料金,納骨料金,仏具価格,個別火葬,合同火葬,永代供養',
    canonical: `${siteUrl}/pricing`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/pricing#webpage`,
      url: `${siteUrl}/pricing`,
      name: '料金表',
      description: 'ペット葬儀・火葬・納骨の料金表。明瞭な料金体系で安心してご利用いただけます。',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'Offer',
              name: '合同火葬（小動物）',
              price: '15000',
              priceCurrency: 'JPY',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'Offer',
              name: '個別火葬（小型ペット）',
              price: '35000',
              priceCurrency: 'JPY',
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@type': 'Offer',
              name: '合同納骨・永代供養',
              price: '10000',
              priceCurrency: 'JPY',
            },
          },
        ],
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <PageHero title="料金表" subtitle="Pricing" />

      {/* 料金テーブルセクション */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
              料金表
            </h1>
            <p className="text-base text-gray-600">
              明瞭な料金体系で安心してご利用いただけます
            </p>
          </div>

          {/* 火葬料金 */}
          <div className="mb-12">
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center">
              <i className="ri-fire-line text-purple-500 mr-3"></i>
              火葬料金
            </h3>
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-purple-200">
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-800">
                        サービス内容
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-800">
                        ペットサイズ
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-gray-800">
                        料金（税込）
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">合同火葬</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        小動物（小鳥・ハムスター等）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥15,000
                      </td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">合同火葬</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        小型ペット（5kg未満）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥20,000
                      </td>
                    </tr>
                    <tr className="hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">合同火葬</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        中型ペット（5kg〜20kg）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥30,000
                      </td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">合同火葬</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        大型ペット（20kg以上）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥50,000〜
                      </td>
                    </tr>
                    <tr className="hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">
                        個別火葬（立ち会い可）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        小動物（小鳥・ハムスター等）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥25,000
                      </td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">
                        個別火葬（立ち会い可）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        小型ペット（5kg未満）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥35,000
                      </td>
                    </tr>
                    <tr className="hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">
                        個別火葬（立ち会い可）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        中型ペット（5kg〜20kg）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥50,000
                      </td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">
                        個別火葬（立ち会い可）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        大型ペット（20kg以上）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥80,000〜
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* 納骨料金 */}
          <div className="mb-12">
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center">
              <i className="ri-building-line text-purple-500 mr-3"></i>
              納骨料金
            </h3>
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-purple-200">
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-800">
                        サービス内容
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-800">
                        詳細
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-bold text-gray-800">
                        料金（税込）
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">
                        合同納骨・永代供養
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        他のペットと共に永代供養
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥10,000
                      </td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">
                        個別納骨棚（3年間）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        専用棚での個別供養
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥30,000
                      </td>
                    </tr>
                    <tr className="hover:bg-purple-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-800">
                        個別納骨棚（延長1年）
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        3年後の延長料金
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 text-right font-medium">
                        ¥10,000
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* 仏具・商品販売 */}
          <div className="mb-12">
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center">
              <i className="ri-store-line text-purple-500 mr-3"></i>
              仏具・商品販売
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* おりん付き祭壇セット */}
              <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/7f99ccc946e9fdc8d6c59a242f3b8b50.jpg"
                    alt="おりん付き祭壇セット"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-base font-bold text-gray-800 mb-2">
                    おりん付き祭壇セット
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    祭壇・おりん・ろうそく立て
                  </p>
                  <p className="text-lg font-bold text-purple-600">¥15,000〜</p>
                </div>
              </div>

              {/* 遺骨カプセル */}
              <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=elegant%20memorial%20ash%20capsule%20pendant%20and%20keychain%20for%20pet%2C%20silver%20and%20gold%20finish%2C%20minimalist%20white%20background%2C%20soft%20lighting%2C%20jewelry%20product%20photography%20style&width=400&height=300&seq=pricing-ash-capsule-002&orientation=landscape"
                    alt="遺骨カプセル"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-base font-bold text-gray-800 mb-2">
                    遺骨カプセル
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    ペンダント型・キーホルダー型
                  </p>
                  <p className="text-lg font-bold text-purple-600">¥5,000〜</p>
                </div>
              </div>

              {/* メモリアル写真立て */}
              <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=elegant%20memorial%20photo%20frame%20for%20pet%2C%20wooden%20and%20glass%20frame%20design%2C%20minimalist%20white%20background%2C%20soft%20natural%20lighting%2C%20home%20decor%20product%20photography&width=400&height=300&seq=pricing-photo-frame-003&orientation=landscape"
                    alt="メモリアル写真立て"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-base font-bold text-gray-800 mb-2">
                    メモリアル写真立て
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    木製フレーム・ガラスフレーム
                  </p>
                  <p className="text-lg font-bold text-purple-600">¥3,000〜</p>
                </div>
              </div>

              {/* 骨壺 */}
              <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=elegant%20ceramic%20pet%20urn%20in%20various%20sizes%2C%20white%20and%20beige%20pottery%2C%20minimalist%20white%20background%2C%20soft%20lighting%2C%20peaceful%20atmosphere%2C%20traditional%20japanese%20style%20product%20photography&width=400&height=300&seq=pricing-urn-004&orientation=landscape"
                    alt="骨壺"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-base font-bold text-gray-800 mb-2">骨壺</h4>
                  <p className="text-sm text-gray-600 mb-4">陶器製・各サイズ</p>
                  <p className="text-lg font-bold text-purple-600">¥5,000〜</p>
                </div>
              </div>
            </div>
          </div>

          {/* 注意事項 */}
          <div className="bg-purple-50 rounded-2xl p-6 sm:p-8">
            <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <i className="ri-information-line text-purple-600 mr-2"></i>
              料金に関する注意事項
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-purple-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span>※ 上記料金はすべて税込表示です。</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-purple-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span>
                  ※
                  大型ペット（20kg以上）の料金は、体重により異なります。詳しくはお問い合わせください。
                </span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-purple-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span>
                  ※ 深夜・早朝（22時〜6時）のご依頼は、別途深夜料金（¥5,000）が発生いたします。
                </span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-purple-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span>
                  ※
                  岡山市外からのご依頼の場合、別途出張費が発生する場合がございます。
                </span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-purple-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span>
                  ※
                  お支払いは現金、クレジットカード、銀行振込に対応しております。
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
